// 🔧 promotion_ai_service.js
// ✅ Purpose: Business service for AI-powered Promotion Engine content generation
// ✅ Features:
// - Calls the secure Firebase Cloud Function
// - Sends campaign business object instead of frontend-built prompts
// - Keeps React pages unaware of AI implementation details
// - Lets Knowledge Engine own LGV context, prompt construction, and claim guardrails
// - Preserves existing Promotion Studio result shape
// - Fully block-commented for clarity and future devs

import { httpsCallable } from "firebase/functions";

import { functions } from "../../firebase";

/**
 * Firebase callable function.
 */
const generatePromotionDraftCallable =
  httpsCallable(
    functions,
    "generatePromotionDraft"
  );

/**
 * Builds the campaign business object sent to the backend.
 *
 * @param {Object} request
 * @return {Object}
 */
const buildCampaign = (request = {}) => {
  const ideaTitle = request.ideaTitle || "";
  const ideaDescription = request.ideaDescription || "";

  return {
    type: request.requestType || "social_post",

    topic:
      request.topic ||
      request.category ||
      request.postType ||
      "general_promotion",

    goal:
      ideaTitle ||
      "Create a Promotion Post",

    platform:
      request.platform || "facebook",

    audience:
      request.audience || "event_attendees",

    eventId:
      request.eventId || null,

    market:
      request.market || "",

    eventType:
      request.eventType || "",

    votingType:
      request.votingType || "",

    language:
      request.language || "en",

    locale:
      request.locale || "en-US",

    region:
      request.region || "us",

    postType:
      request.postType || "",

    category:
      request.category || "",

    notes:
      request.userInstruction ||
      `${ideaTitle}\n\n${ideaDescription}`.trim(),
  };
};

/**
 * ============================================================================
 * generatePromotionContent
 * ============================================================================
 *
 * Generates AI content for Promotion Studio.
 */
export const generatePromotionContent = async (
  request
) => {
  const response =
    await generatePromotionDraftCallable({
      campaign:
        buildCampaign(request),

      promptVersion:
        2,
      postId: request.postId || null,
      draftKey: request.draftKey || null,
    });
    // 🔐 Full callable response intentionally not logged; it may contain generated campaign content.
  return {
    aiUsage: response.data.aiUsage,
    promptVersion:
      response.data.promptVersion,

    content:
      response.data.content,

    validation: {
      valid: true,
      errors: [],
      warnings: [],
    },

    metadata:
      response.data.metadata,

    diagnostics:
      response.data.diagnostics || {},
  };
};
